import React, { useMemo } from 'react';
import { Stack } from '@mui/material';
import { DealTypes } from 'api';
import { colors } from 'theme/constants';
import {
  formatCurrency,
  formatNumber,
  formatPercent,
} from 'helpers/formatters';
import { AttributionModelEnum } from 'types/common';
import { getAttributionModelField } from 'helpers/common';
import { DealInfoItems } from '../DetailsTab';
import InfoCard from '../InfoCard';

interface Props {
  deal: DealTypes.ItemDataExtended;
  attributionModel: AttributionModelEnum;
}

const InfoCardsStack = (props: Props): JSX.Element | null => {
  const { deal, attributionModel } = props;

  const actualBudgetInfo: DealInfoItems = useMemo(
    () => [
      {
        key: 'budget_type',
        label: 'Budget type',
        value: deal.budget_type,
      },
      {
        key: 'budget_actual_lc',
        label: 'Budget',
        value:
          deal.budget_actual_lc && deal.budget_actual_lc >= 0
            ? formatCurrency(deal.budget_actual_lc)
            : '-',
      },
      {
        key: 'budget_fixed',
        label: 'Budget fixed',
        value: deal.budget_fixed,
      },
      {
        label: 'ROI',
        key: getAttributionModelField(attributionModel, 'roi_actual'),
        labelCaption: `(${attributionModel})`,
        value: deal[getAttributionModelField(attributionModel, 'roi_actual')],
      },
      {
        label: 'Revenue',
        key: getAttributionModelField(attributionModel, 'revenue_actual'),
        labelCaption: `(${attributionModel})`,
        value: formatCurrency(
          deal[getAttributionModelField(attributionModel, 'revenue_actual')],
        ),
      },
      {
        key: 'commission',
        label: 'Commission',
        value: deal.commission ?? '-',
      },
    ],
    [attributionModel, deal],
  );

  const basketInfo: DealInfoItems = useMemo(
    () => [
      {
        label: 'Average basket',
        key: getAttributionModelField(attributionModel, 'avg_basket_actual'),
        labelCaption: `(${attributionModel})`,
        value: formatCurrency(
          deal[getAttributionModelField(attributionModel, 'avg_basket_actual')],
        ),
      },
      {
        label: 'Orders count',
        key: getAttributionModelField(attributionModel, 'orders_nb_actual'),
        labelCaption: `(${attributionModel})`,
        value:
          deal[getAttributionModelField(attributionModel, 'orders_nb_actual')],
      },
      {
        label: 'Total customers',
        key: `${getAttributionModelField(attributionModel, 'customers')}.all`,
        labelCaption: `(${attributionModel})`,
        value:
          deal[
            getAttributionModelField<
              DealTypes.ItemDataExtended,
              typeof attributionModel,
              'customers'
            >(attributionModel, 'customers')
          ].all,
      },
      {
        label: 'New customers',
        key: `${getAttributionModelField(attributionModel, 'customers')}.new`,
        labelCaption: `(${attributionModel})`,
        value:
          deal[
            getAttributionModelField<
              DealTypes.ItemDataExtended,
              typeof attributionModel,
              'customers'
            >(attributionModel, 'customers')
          ].new,
      },
      {
        label: 'NCR',
        key: getAttributionModelField(attributionModel, 'new_customers_ratio'),
        labelCaption: `(${attributionModel})`,
        value: formatPercent(
          deal[
            getAttributionModelField(attributionModel, 'new_customers_ratio')
          ],
        ),
      },
      {
        label: 'Reactivation rate',
        key: getAttributionModelField(
          attributionModel,
          'customer_reactivation_ratio',
        ),
        labelCaption: `(${attributionModel})`,
        value: formatPercent(
          deal[
            getAttributionModelField(
              attributionModel,
              'customer_reactivation_ratio',
            )
          ],
        ),
      },
    ],
    [deal, attributionModel],
  );

  const advancedMetricsInfo: DealInfoItems = useMemo(
    () => [
      {
        label: 'CM3',
        key: getAttributionModelField(attributionModel, 'cm3_actual'),
        labelCaption: `(${attributionModel})`,
        value: formatCurrency(
          deal[getAttributionModelField(attributionModel, 'cm3_actual')],
        ),
      },
      {
        label: 'CM3%',
        key: getAttributionModelField(attributionModel, 'cm3_actual_pc'),
        labelCaption: `(${attributionModel})`,
        value: formatPercent(
          deal[getAttributionModelField(attributionModel, 'cm3_actual_pc')],
        ),
      },
      {
        key: 'cpm_actual',
        label: 'CPM',
        value: formatCurrency(deal.cpm_actual),
      },
      {
        key: 'calculated_cpm',
        label: 'Actual CPM',
        value: formatCurrency(deal.calculated_cpm),
      },
      {
        label: 'CPO',
        key: getAttributionModelField(attributionModel, 'cpo_actual'),
        labelCaption: `(${attributionModel})`,
        value: formatCurrency(
          deal[getAttributionModelField(attributionModel, 'cpo_actual')],
        ),
      },
      {
        label: 'Gross profit',
        key: getAttributionModelField(attributionModel, 'gross_profit_actual'),
        labelCaption: `(${attributionModel})`,
        value: formatCurrency(
          deal[
            getAttributionModelField(attributionModel, 'gross_profit_actual')
          ],
        ),
      },
    ],
    [deal, attributionModel],
  );

  const influencerInsightsInfo: DealInfoItems = useMemo(
    () => [
      {
        key: 'actual_reach',
        label: 'Actual reach',
        value: deal.actual_reach,
      },
      {
        key: 'avg_overall_reach',
        label: 'Avg overall reach',
        value: deal.avg_overall_reach,
      },
      {
        key: 'clicks',
        label: 'Clicks',
        value: deal.clicks,
      },
      {
        key: 'impressions',
        label: 'Impressions',
        value: deal.impressions,
      },
      {
        key: 'conversion_rate_pc',
        label: 'Conversion rate %',
        value: formatPercent(deal.conversion_rate_pc),
      },
      {
        key: 'ctr',
        label: 'CTR',
        value: formatNumber(deal.ctr),
      },
      {
        key: 'rpm',
        label: 'RPM',
        value: formatCurrency(deal.rpm),
      },
      {
        key: 'engagement',
        label: 'Engagement',
        value: formatNumber(deal.engagement),
      },
      {
        key: 'engagement_rate_pc',
        label: 'Engagement Rate %',
        value: formatPercent(deal.engagement_rate_pc),
      },
    ],
    [deal],
  );

  return (
    <Stack rowGap={3}>
      <InfoCard
        title="Actual budget info"
        data={actualBudgetInfo}
        color={colors.darkGreen}
      />
      <InfoCard title="Basket info" data={basketInfo} color={colors.blue} />
      <InfoCard
        title="Advanced metrics"
        data={advancedMetricsInfo}
        color={colors.darkRed}
      />
      <InfoCard
        title="Influencer insights info"
        data={influencerInsightsInfo}
        color={colors.blue}
      />
    </Stack>
  );
};

export default InfoCardsStack;
